import { Children, forwardRef, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';

import { isSVGElement } from '@core';

import { Heading2, Heading3, Heading4 } from '../Text';

import * as css from './ActivityNavigation.css';
import { ActivityItemMenu, ActivityNavigationItem, ActivityNavigationItemStatus } from './components';

type ActivityListHeaderProps = PropsWithChildren<{ subtitle: ReactNode; menu?: ReactNode }>;
const ActivityListHeader = ({ children, subtitle, menu: Menu }: ActivityListHeaderProps) => {
    return (
        <div className={css.header}>
            <div className={css.title}>
                <Heading3>{children}</Heading3>
                {Menu}
            </div>

            <div className={css.activityNavigationParameter}>{subtitle}</div>
        </div>
    );
};

type ActivityListProps = PropsWithChildren<{
    title: string;
    subtitle: ReactNode;
    menu?: ReactNode;
}>;

export const ActivityList = forwardRef<HTMLUListElement, ActivityListProps>(
    ({ title, subtitle, children, menu }, ref) => {
        const childrenCount = Children.count(children);

        return (
            <aside className={css.aside}>
                <ActivityListHeader subtitle={subtitle} menu={menu}>
                    {title}
                </ActivityListHeader>

                <ul ref={ref} className={css.list}>
                    {childrenCount ? children : <ActivityListEmptyItem />}
                </ul>
            </aside>
        );
    },
);

/**
 * Visual and state flags that control how an {@link ActivityListItem} is rendered.
 */
export interface ActivityListItemFlags {
    /**
     * Indicates that this activity contains unread notifications.
     * Used to render unread state in the item menu or indicator badge.
     */
    hasUnreadNotification: boolean;

    /**
     * Indicates that this activity has a discussion thread.
     * Enables thread-related UI in {@link ActivityItemMenu}.
     */
    hasThread: boolean;

    /**
     * Marks this item as currently selected.
     * Affects styling and ARIA attributes.
     */
    isSelected?: boolean;

    /**
     * Marks this item as being compared against another activity.
     * Used for comparison-specific styling or indicators.
     */
    isComparison?: boolean;

    /**
     * Marks this activity as a favorite.
     * Enables favorite indicator styling.
     */
    isFavorite?: boolean;
}

/**
 * Props for rendering a single activity inside {@link ActivityList}.
 */
type ActivityListItemProps = {
    /**
     * Unique identifier for the activity.
     * Used for DOM attributes and selection handling.
     */
    id: string;

    /**
     * Callback invoked when the activity item is selected.
     * Triggered on click (excluding interaction with nested controls).
     */
    onSelect?: () => void;

    /**
     * Status of the activity.
     * Determines which visual state is rendered in {@link ActivityNavigationItem}.
     */
    status: ActivityNavigationItemStatus;

    /**
     * Sequential or display number associated with the activity.
     * Typically rendered as part of the activity indicator.
     */
    number: number;

    /**
     * Primary value displayed for the activity.
     * Falls back to an em dash when undefined.
     */
    value?: string;

    /**
     * Size of the activity icon in pixels.
     * Defaults to 14.
     */
    iconSize?: number;

    /**
     * Optional menu content rendered in {@link ActivityItemMenu}.
     */
    menu?: ReactNode;

    /**
     * Visual and state flags controlling how the activity item is rendered.
     */
    flags: ActivityListItemFlags;
};

export const ActivityListItem = ({
    id,
    onSelect,
    status,
    number,
    value,
    iconSize = 14,
    menu,
    flags,
}: ActivityListItemProps) => {
    const { isSelected, hasThread, hasUnreadNotification, isComparison, isFavorite } = flags;
    return (
        <li
            data-swing-id={id}
            // TODO(a11y): It is NOT fully accessible (no keyboard interaction, no native semantics).
            // <li><button type="button">...</button></li>
            // and move interaction handling to the button.
            role="button"
            className={css.item({ isSelected })}
            onClick={(e) => {
                e.stopPropagation();

                const target = e.target as Element;

                if (isSVGElement(target)) {
                    // Prevent selecting the swing underneath this icon
                    return;
                }

                onSelect?.();
            }}
        >
            <ActivityNavigationItem
                id={id}
                status={status}
                number={number}
                iconSize={iconSize}
                isComparison={isComparison}
                isFavorite={isFavorite}
            />
            <Heading2>{value ?? '—'}</Heading2>
            <ActivityItemMenu menu={menu} hasThread={hasThread} hasUnreadNotification={hasUnreadNotification} />
        </li>
    );
};

type WithOnClick = { onClick?: MouseEventHandler<HTMLElement> };

export const ActivityListEmptyItem = ({ isSelected, onClick }: { isSelected?: boolean } & WithOnClick) => {
    return (
        <ActivityListBaseItem isSelected={isSelected} onClick={onClick}>
            <Heading4>—</Heading4>
        </ActivityListBaseItem>
    );
};

export const ActivityListBaseItem = ({
    children,
    isSelected = false,
    onClick,
}: PropsWithChildren<{ isSelected?: boolean } & WithOnClick>) => {
    return (
        <li className={css.item({ isSelected: isSelected })} onClick={onClick}>
            <div className={css.baseItem}>{children}</div>
        </li>
    );
};
